/**
 * Factory Domain Schemas
 *
 * Factory entity schemas with inferred TypeScript types.
 * Represents physical manufacturing locations with tracking infrastructure.
 *
 * Aligned with backend factory.model.ts
 */

import { z } from 'zod';
import { isValidTimezone } from '@/utils/timezone';
import { DAY_PRESETS } from '@/stores/atoms/analytics.atoms';

// --- Shifts (per weekday) ---------------------------------------------------

export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

const TIME_HHMM_REGEX = /^([01]\d|2[0-4]):([0-5]\d)$/;

/**
 * Parse an HH:mm string to minutes since 00:00 (24:00 is normalized to 1440).
 */
function hhmmToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Convert a shift `{ start, end }` to one or two minute-segments on a single
 * 0..1440 axis. A midnight-crossing shift (end <= start) is split into two
 * segments: [start, 1440) and [0, end).
 */
function shiftSegments(start: string, end: string): Array<[number, number]> {
  const s = hhmmToMinutes(start);
  const e = hhmmToMinutes(end);
  if (e > s) {
    return [[s, e]];
  }
  // Midnight-crossing
  return [
    [s, 1440],
    [0, e],
  ];
}

export const shiftSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1).max(40),
    start: z.string().regex(TIME_HHMM_REGEX, 'factory.validation.invalid_time_format'),
    end: z.string().regex(TIME_HHMM_REGEX, 'factory.validation.invalid_time_format'),
  })
  .refine(
    shift => {
      // Allow 'start === end' to be rejected (zero-length shift)
      const s = hhmmToMinutes(shift.start);
      const e = hhmmToMinutes(shift.end);
      return s !== e;
    },
    { message: 'factory.validation.shift_zero_length', path: ['end'] }
  );

/**
 * Validate a per-day shift list: ≤5 entries, no overlapping windows (taking
 * midnight-crossing shifts into account).
 */
const dayShiftsSchema = z
  .array(shiftSchema)
  .max(5, 'factory.validation.shift_max_per_day')
  .refine(
    shifts => {
      // Expand to minute-segments and detect overlaps pairwise
      const segments: Array<[number, number]> = shifts.flatMap(s => shiftSegments(s.start, s.end));
      for (let i = 0; i < segments.length; i += 1) {
        for (let j = i + 1; j < segments.length; j += 1) {
          const [aStart, aEnd] = segments[i];
          const [bStart, bEnd] = segments[j];
          if (aStart < bEnd && bStart < aEnd) {
            return false;
          }
        }
      }
      return true;
    },
    { message: 'factory.validation.shift_overlap' }
  );

export const weeklyShiftsSchema = z.object({
  mon: dayShiftsSchema.default([]),
  tue: dayShiftsSchema.default([]),
  wed: dayShiftsSchema.default([]),
  thu: dayShiftsSchema.default([]),
  fri: dayShiftsSchema.default([]),
  sat: dayShiftsSchema.default([]),
  sun: dayShiftsSchema.default([]),
});

// --- Day presets ------------------------------------------------------------

const dayPresetSchema = z.enum(DAY_PRESETS);

export const dayPresetsSchema = z
  .object({
    enabled: z.array(dayPresetSchema).min(1, 'factory.validation.day_preset_min_one'),
    default: dayPresetSchema,
  })
  .refine(cfg => cfg.enabled.includes('custom'), {
    message: 'factory.validation.day_preset_custom_required',
    path: ['enabled'],
  })
  .refine(cfg => cfg.enabled.includes(cfg.default), {
    message: 'factory.validation.day_preset_default_must_be_enabled',
    path: ['default'],
  });

// --- Factory settings (extended) -------------------------------------------

// Factory settings schema (matches backend FactorySettings)
export const factorySettingsSchema = z.object({
  timezone: z.string().default('UTC'),
  workingHours: z
    .object({
      start: z.string().regex(/^\d{2}:\d{2}$/, 'factory.validation.invalid_time_format'),
      end: z.string().regex(/^\d{2}:\d{2}$/, 'factory.validation.invalid_time_format'),
    })
    .refine(data => data.end > data.start, {
      message: 'factory.validation.end_after_start',
      path: ['end'],
    })
    .optional(),
  alertsEnabled: z.boolean().default(true),
  positionUpdateInterval: z.number().min(100).max(10000).default(1000),
  maxAssets: z.number().min(1).default(1000),
  weeklyShifts: weeklyShiftsSchema.optional(),
  dayPresets: dayPresetsSchema.optional(),
});

// Factory location schema (matches backend)
export const factoryLocationSchema = z.object({
  address: z.string().min(1, 'factory.validation.address_required'),
  city: z.string().min(1, 'factory.validation.city_required'),
  country: z.string().min(1, 'factory.validation.country_required'),
  postalCode: z.string().min(1, 'factory.validation.postal_code_required'),
  coordinates: z.tuple([z.number(), z.number()]).optional(), // [longitude, latitude]
});

// Factory floor plan schema
export const factoryFloorPlanSchema = z.object({
  url: z.string().url().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  scale: z.number().optional(),
});

// Factory-level asset type configuration (Record from backend: type → { color, icon? })
export const factoryAssetTypeConfigSchema = z.record(
  z.string(),
  z.object({
    color: z.string(),
    icon: z.string().optional(),
  })
);

// Factory schema - core factory entity (matches backend)
// IDs are slug-style strings (e.g. 'simoldes-spf'), not UUIDs.
// Timestamps are ISO strings from the API, coerced to Date.
export const factorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1, 'factory.validation.name_required').max(200),
  tenantId: z.string().min(1),
  location: factoryLocationSchema,
  settings: factorySettingsSchema,
  floorPlan: factoryFloorPlanSchema.optional(),
  assetTypeConfig: factoryAssetTypeConfigSchema.optional(),
  isActive: z.boolean().default(true),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Factory creation form schema
const factoryFormSettingsSchema = factorySettingsSchema.extend({
  timezone: z
    .string()
    .default('UTC')
    .refine(isValidTimezone, { message: 'factory.validation.invalid_timezone' }),
});

export const factoryCreateFormSchema = z.object({
  name: z.string().min(1, 'factory.validation.name_required').max(200),
  location: factoryLocationSchema,
  settings: factoryFormSettingsSchema.optional(),
  floorPlan: factoryFloorPlanSchema.optional(),
  assetTypeConfig: factoryAssetTypeConfigSchema.optional(),
  isActive: z.boolean().default(true),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// Factory update form schema
export const factoryUpdateFormSchema = factoryCreateFormSchema.partial();

// Factory filter schema for search/filtering
export const factoryFilterSchema = z.object({
  search: z.string().optional(),
  country: z.string().optional(),
  isActive: z.boolean().optional(),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

// Factory list response schema
// Backend returns { factories, total } — page/limit/hasMore are not included.
export const factoryListResponseSchema = z.object({
  factories: z.array(factorySchema),
  total: z.number(),
  page: z.number().optional(),
  limit: z.number().optional(),
  hasMore: z.boolean().optional(),
});

// Inferred TypeScript types - single source of truth
export type FactoryAssetTypeConfig = z.infer<typeof factoryAssetTypeConfigSchema>;
export type FactorySettings = z.infer<typeof factorySettingsSchema>;
export type FactoryLocation = z.infer<typeof factoryLocationSchema>;
export type FactoryFloorPlan = z.infer<typeof factoryFloorPlanSchema>;
export type Factory = z.infer<typeof factorySchema>;
export type FactoryCreateForm = z.infer<typeof factoryCreateFormSchema>;
export type FactoryUpdateForm = z.infer<typeof factoryUpdateFormSchema>;
export type FactoryFilter = z.infer<typeof factoryFilterSchema>;
export type FactoryListResponse = z.infer<typeof factoryListResponseSchema>;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];
export type Shift = z.infer<typeof shiftSchema>;
export type WeeklyShifts = z.infer<typeof weeklyShiftsSchema>;
export type FactoryDayPresets = z.infer<typeof dayPresetsSchema>;

// Validation helpers
export const validateFactory = (factory: unknown): factory is Factory => {
  return factorySchema.safeParse(factory).success;
};

export const validateFactoryId = (factoryId: unknown): factoryId is string => {
  return z.string().min(1).safeParse(factoryId).success;
};
